<template>
    <div class="details-col">
        <!-- Renewal section -->
        <div v-if="membership.isRenewable() || membership.isRenewing()"
             class="details-col__actions mt-1 mb-1">
            <div v-if="membership.isRenewable()">
                <!-- Renew membership -->
                <button type="button"
                        class="btn"
                        :class="[membership.isRenewing() ? 'btn-success active' : 'btn-primary']"
                        :disabled="renewalDateModalOpen"
                        @click="handleChangeRenewingState">
                    <span>
                        <i v-show="renewalDateModalOpen"
                           class="fa-solid fa-arrows-rotate fa-spin"></i>
                        <i v-show="!renewalDateModalOpen"
                           class="fa-solid fa-arrow-rotate-right"></i>
                        {{
                            l(membership.getRenewButtonText(), {
                                from: membership.getChosenRenewalFromDate(),
                                nextEndDate: membership.renewToDate
                            })
                        }}
                        ({{ currencyFilter(membership.renewalPlan.getPrice()) }})
                    </span>
                </button>
            </div>

            <div v-if="membership.isRenewing()">
                <!-- Confirm renewal -->
                <button class="btn btn-primary"
                        @click="handleTakePaymentForRenewal">
                    <i class="fa-solid fa-arrow-right"></i>
                    {{ l('MEMBERSHIPS.CONTINUE_RENEWAL') }}
                </button>
            </div>
        </div>

        <!-- Membership details -->
        <div class="details-col__details">
            <!-- Membership Ref -->
            <div class="larger-detail hidden-xs">
                <i class="fa-solid fa-barcode"></i>
                <h6>{{ l('MEMBERSHIP_DETAILS.REF_LABEL') }}</h6>
                <p>{{ membership.ref }}</p>
            </div>

            <!-- Membership Plan -->
            <div v-if="membershipSubscription">
                <i class="fa-solid fa-ballot"></i>
                <h6>{{ l('MEMBERSHIP_DETAILS.CURRENT_PLAN') }}</h6>
                <p>{{ membershipSubscription.membershipPlan.name }}</p>
            </div>

            <!-- If the membership is active but with a future start date, show that -->
            <div v-if="membership.isPostponed() && membership.isActive()">
                <i class="fa-solid fa-exclamation-circle text-warning"></i>
                <h6>{{ l('MEMBERSHIP_DETAILS.VALID_FROM_LABEL') }}</h6>
                <p class="text-warning">
                    {{ dateFilter(membership.getInitialStartDate()) }}
                </p>
            </div>
            <!-- Show status normally -->
            <div v-else>
                <i v-if="membership.isActive()"
                   class="fa-solid fa-check-circle text-success"></i>
                <i v-else
                   class="fa-solid fa-times-circle text-danger"></i>
                <h6>{{ l('MEMBERSHIP_DETAILS.STATUS_LABEL') }}</h6>
                <p :class="{
                    'text-success': membership.isActive(),
                    'text-danger': !membership.isActive()
                }">
                    {{ l('MEMBERSHIP_DETAILS.' + membership.getStatus()) }}
                </p>
            </div>

            <!-- Expiry date -->
            <div v-if="membership.isActive()">
                <i class="fa-solid fa-hourglass"
                   :class="{ 'text-warning' : warnAboutExpiry }"></i>
                <h6>{{ l('MEMBERSHIP_DETAILS.EXPIRES_LABEL') }}</h6>
                <p :class="{ 'text-warning' : warnAboutExpiry }">
                    {{ dateFilter(membership.maxSubscriptionExpiryDate) }}
                    <br />
                    <small>({{ ucfirstFilter(timeRemainingBeforeExpiry) }})</small>
                </p>
            </div>

            <!-- Auto-renewal status -->
            <div v-if="membership.isActive()">
                <i class="fa-solid fa-arrow-rotate-right"
                   :class="{
                       'text-success': membership.willAutoRenew(),
                       'text-warning': !membership.willAutoRenew()
                   }"></i>
                <h6>{{ l('MEMBERSHIP_DETAILS.AUTO_RENEWAL_LABEL') }}</h6>
                <p>
                    <span v-if="!membership.willAutoRenew()"
                          class="text-warning">{{ l('MANAGE_ACCOUNT.AUTO_RENEW_OFF') }}</span>
                    <span v-else
                          class="text-success">{{ l('MANAGE_ACCOUNT.AUTO_RENEW_ON') }}</span>
                </p>
            </div>

            <!-- Customer email -->
            <div v-if="membership.customerAccount && membership.customerAccount.email">
                <i class="fa-solid fa-envelope"></i>
                <h6>{{ l('MEMBERSHIP_DETAILS.EMAIL_LABEL') }}</h6>
                <p>{{ membership.customerAccount.email }}</p>
            </div>

            <!-- Customer address -->
            <div v-if="membership.address && membership.address.getFormattedAddress()">
                <i class="fa-solid fa-house"></i>
                <h6>{{ l('MEMBERSHIP_DETAILS.ADDRESS_LABEL') }}</h6>
                <p>{{ addressFilter(membership.address) }}</p>
            </div>
        </div>

        <!-- Membership action buttons -->
        <div class="details-col__actions">
            <!-- Edit details button -->
            <div>
                <button type="button"
                        class="btn btn-default"
                        :disabled="membership.currentlyProcessing || !membership.isActive()"
                        @click="$emit('amend-members')">
                    <i class="fa-solid fa-pen-to-square"></i>
                    {{ l('MEMBERSHIP_DETAILS.EDIT_DETAILS') }}
                </button>
            </div>

            <!-- View account button-->
            <div v-if="viewAccountPath">
                <a :href="viewAccountPath"
                   class="btn btn-default">
                    <i class="fa-solid fa-user"></i>
                    {{ l('MEMBERSHIP_DETAILS.ACCOUNT_BTN_TEXT') }}
                </a>
            </div>

            <!-- Cancel membership button -->
            <div>
                <button class="btn btn-default"
                        :disabled="membership.isRenewing() || !membership.isActive()"
                        @click="$emit('cancel-membership')">
                    <i class="fa-solid fa-circle-x"></i>
                    {{ l('MEMBERSHIP_DETAILS.CANCEL_BTN_TEXT') }}
                </button>
            </div>

            <!-- Change plan button -->
            <div>
                <button class="btn btn-default"
                        :disabled="membership.isRenewing() || !membership.isActive()"
                        @click="$emit('change-plan')">
                    <i class="fa-solid fa-right-left"></i>
                    {{ l('MEMBERSHIP_DETAILS.CHANGE_PLAN_BTN') }}
                </button>
            </div>

            <!-- Stop renewing button -->
            <div v-if="membership.willAutoRenew()">
                <button class="btn btn-default"
                        :disabled="stopRenewingInProgress"
                        @click="$emit('stop-renewing')">
                    <i v-if="stopRenewingInProgress"
                       class="fa-solid fa-arrows-rotate fa-spin"></i>
                    <i v-else
                       class="fa-solid fa-stop"></i>
                    {{ l('MEMBERSHIP_DETAILS.STOP_RENEWING_BTN') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment/moment';
import { addressFilter } from '@/vue/filters/addressFilter';
import { container } from '@/container';
import { currencyFilter } from '@/vue/filters/currencyFilter';
import { dateFilter } from '@/vue/filters/dateFilter';
import { ucfirstFilter } from '@/vue/filters/ucfirstFilter';

export default {
    props: {
        /**
         * @type {Membership}
         */
        membership: {
            type: Object,
            required: true,
        },

        /**
         * @type {MembershipSubscription}
         */
        membershipSubscription: {
            type: Object,
            required: false,
            default: null,
        },

        /**
         * @type {boolean}
         */
        stopRenewingInProgress: {
            type: Boolean,
            default: false,
        },
    },

    emits: [
        'amend-members',
        'cancel-membership',
        'change-plan',
        'stop-renewing',
    ],

    computed: {
        renewalDateModalOpen() {
            // TODO: Better in Vuex.
            return container.membershipManagementService.renewalDateModalOpen;
        },

        timeRemainingBeforeExpiry() {
            if (!this.membership) {
                return '';
            }

            return moment(this.membership.maxSubscriptionExpiryDate).fromNow();
        },

        viewAccountPath() {
            if (this.membership.customerAccount) {
                return `#/accounts/${this.membership.customerAccount.ID}`;
            }
            return null;
        },

        warnAboutExpiry() {
            if (!this.membership) {
                return false;
            }
            const now = moment();
            const then = moment(this.membership.maxSubscriptionExpiryDate);
            const daysLeft = then.diff(now, 'days');
            return daysLeft <= 30;
        },
    },

    methods: {
        addressFilter,
        currencyFilter,
        dateFilter,
        ucfirstFilter,

        handleChangeRenewingState() {
            container.membershipManagementService.changeMembershipRenewingState(this.membership, false);
        },

        handleTakePaymentForRenewal() {
            container.membershipManagementService.takePaymentForRenewingMemberships([this.membership]);
        },
    },
};
</script>
